import React from 'react';
import PropTypes from 'prop-types';
import { RouteContext } from './Route';
import api from '../utils/api';
import { LocalizationContext } from './Localization';
import { LocationContext } from './Location';
import { SettingsContext } from './Settings';
import Router from '../routes';
import { shortId } from '../utils';
import { getSchoolByShortIdWizard, getCourseByShortIdWizard } from '../utils/directory';
import { UserContext } from './User';

export const EditorWizardContext = React.createContext();

export const EditorWizardProvider = ({ children }) => {
  const { locale: pageLocale } = React.useContext(LocalizationContext);
  const { params, page } = React.useContext(RouteContext);
  const { user, refreshUser } = React.useContext(UserContext);
  const {
    domainSettings: {
      defaultCurrency,
      timezone,
      schoolId,
      onboardingCourseId,
      featureTermsAndConditions,
    },
  } = React.useContext(SettingsContext);
  const {
    location: { center: defaultCenter },
  } = React.useContext(LocationContext);

  const [step, setStep] = React.useState(featureTermsAndConditions ? 0 : 1);
  const [hideHeaderBar, setHideHeaderBar] = React.useState(false);
  const [edited, setEditedDefault] = React.useState({});
  const [locale, setLocale] = React.useState('default');
  const [loading, setLoading] = React.useState(false);
  const [notFound, setNotFound] = React.useState(false);
  const [createdCourse, setCreatedCourse] = React.useState({});
  const [createdSchool, setCreatedSchool] = React.useState({});

  const {
    coursetypes,
    language,
    price,
    period,
    currency,
    title,
    description,
    schoolTitle,
    email,
    schoolImage,
    courseImage,
    location,
    courseShortId,
    terms,
    // draft,
    period_value: periodValue,
    free_course: freeCourse,
    phone,
    accepted,
    privacy,
    trainingType,
    schedules,
  } = edited;

  const urlStep = [
    'terms',
    'training-type',
    'select-category',
    'title-description',
    'price',
    'user-contacts',
    'select-image',
    'school-location',
    'finish',
  ];

  const isFirstDataFillStepPassed = stepToCheck => stepToCheck > 1;
  const isFinalDataFillStep = stepToCheck => stepToCheck === urlStep.length - 2;
  const isFinished = stepToCheck => stepToCheck === urlStep.length - 1;

  const isDraft = stepToCheck => !isFinalDataFillStep(stepToCheck);
  const getStepFromParams = () => params.step > 0 ? Number(params.step) : urlStep.indexOf(params.step);
  const getCourseInitData = async () => {
    let courseData = {
      courseShortId: shortId()
    };

    if (params.courseShortId) {
      const fetchedCourse = await getCourseByShortIdWizard(params.courseShortId);

      if (fetchedCourse?.course) {
        courseData = {
          ...courseData,
          courseShortId: params.courseShortId,
          fetchedCourse: fetchedCourse.course,
          courseImage: fetchedCourse.course.image,
          description: fetchedCourse.course.description,
          trainingType: fetchedCourse.course.trainingType,
          language: fetchedCourse.course.language,
          category: fetchedCourse.course.category,
          coursetypes: fetchedCourse.course.coursetypes,
          title: fetchedCourse.course.title,
          [`title_${pageLocale}`]: fetchedCourse.course.[`title_${pageLocale}`],
          free_course: fetchedCourse.course.free_course,
          schedules: fetchedCourse.course.schedules,
          currency: fetchedCourse.course.currency,
          period_value: fetchedCourse.course.period_value,
          period: fetchedCourse.course.period,
          price: fetchedCourse.course.price,
        };
      }
    }

    return courseData;
  };

  const prepareLocationData = (fetchedSchool) => {
    let location = {};

    const firstLocation = fetchedSchool.school?.locations[0] || {};

    if (firstLocation.Latitude && firstLocation.Longitude) {
      location = {
        ...firstLocation,
        lat: firstLocation.Latitude,
        lng: firstLocation.Longitude
      };
    }

    return location;
  }

  const getSchoolInitData = async () => {
    let schoolData = {
      shortId: shortId(),
      currency: defaultCurrency,
      location: { lat: defaultCenter.lat, lng: defaultCenter.lng },
    };

    if (params.shortId) {
      const fetchedSchool = await getSchoolByShortIdWizard(params.shortId);

      if (fetchedSchool?.school) {
        schoolData = {
          ...schoolData,
          fetchedSchool: fetchedSchool.school,
          schoolImage: fetchedSchool.school.logo,
          schoolTitle: fetchedSchool.school.Name,
          email: fetchedSchool.school.email,
          phone: fetchedSchool.school.phone,
          location: {
            ...schoolData.location,
            ...prepareLocationData(fetchedSchool)
          },
          shortId: params.shortId,
        };
      }
    }

    return schoolData;
  };

  const initWizardData = async () => {
    const schoolInitData = await getSchoolInitData();
    const courseInitData = await getCourseInitData();
    const stepFromParams = getStepFromParams();

    setEditedDefault(prev => ({
      ...prev,
      ...schoolInitData,
      ...courseInitData,
      draft: true,
      step: stepFromParams,
    }));

    // in case user come direct url(or changed url params) we will show 404 if there is no such school:course
    if (isFirstDataFillStepPassed(stepFromParams) && (
      params.shortId && !schoolInitData.fetchedSchool ||
      params.courseShortId && !courseInitData.fetchedCourse
    )) {
      setNotFound(true);
    }

    setLoading(false);
  }

  React.useEffect(() => {
    if (params.step) {
      const urlIndex = urlStep.indexOf(params.step);
      if (urlIndex === -1) {
        setStep(Number(params.step));
      } else {
        setStep(urlIndex);
      }
    }
    if ((params.step === urlStep[1] || params.step === 1) && user) {
      const { providerId, email: userEmail, firstName, lastName } = user;
      setEditedDefault(prev => ({
        ...prev,
        terms: {
          providerId,
          email: userEmail,
          firstName,
          lastName,
        },
      }));
    }
  }, [params.step]);

  React.useEffect(() => {
    setLoading(true);

    if (user) {
      if (!params.shortId && user.schools && user.schools[0]) {
        Router.pushRoute('createCourseSchool', {
          locale: pageLocale,
          step: 1,
          shortId: user.schools[0].shortId,
        });
      } else {
        initWizardData();
      }
    }
  }, [user, params.shortId]);

  React.useEffect(() => {
    if (isFinished(step)) {
      (async () => {
        const courseData = await getCourseByShortIdWizard(params.courseShortId);
        const schoolData = await getSchoolByShortIdWizard(params.shortId);
        setCreatedCourse(courseData.course);
        setCreatedSchool(schoolData.school);
      })();
    }
  }, [step]);

  const updateContent = (key, data) => {
    setEditedDefault(prev => ({
      ...prev,
      [key]: data,
    }));
  };

  const saveCourse = async () => {
    const school = {
      title: schoolTitle,
      shortId: edited.shortId,
      logo: schoolImage,
      email,
      location,
      timezone,
      terms,
      draft: isDraft(step),
      step,
      schoolId,
      phone,
      trainingType,
      steps: urlStep.length - 1,
    };

    const course = {
      category: edited.category && { _id: edited.category.id, id: edited.category.id },
      coursetypes: coursetypes && { id: coursetypes.id },
      language: language && { _id: language.id, id: language.id },
      price,
      period,
      period_value: periodValue,
      currency,
      title,
      description,
      image: courseImage,
      shortId: courseShortId,
      draft: isDraft(step),
      step,
      [`title_${pageLocale}`]: title,
      free_course: freeCourse,
      schoolId,
      trainingType,
      steps: urlStep.length - 1,
      schedules,
    };

    await api({
      method: 'put',
      data: { school, course, onboardingCourseId },
      url: `courses/${courseShortId}/updateWizard`,
    });

    if (step >= urlStep.length - 2) {
      await refreshUser();
    }
  };

  const previousStep = () => {
    const newStep = step - 1;
    const sequenceUrl = urlStep[newStep];
    if (newStep) {
      Router.pushRoute('createCourseSchool', {
        locale: pageLocale,
        step: sequenceUrl,
        shortId: edited.shortId,
        courseShortId,
      });
    } else {
      Router.pushRoute('addCourseIntro', {
        locale: pageLocale,
      });
    }
  };

  const nextStep = async () => {
    if (!user) {
      return;
    }
    const newStep = +step + 1;
    const sequenceUrl = urlStep[newStep];
    if (newStep >= 1) {
      setLoading(true);
      if (newStep !== 1) {
        await saveCourse();
      }
      setLoading(false);
      Router.pushRoute('createCourseSchool', {
        locale: pageLocale,
        step: sequenceUrl,
        shortId: edited.shortId,
        courseShortId,
      });
    } else {
      Router.pushRoute('createCourseSchool', {
        locale: pageLocale,
        step: newStep,
        shortId: edited.shortId,
        courseShortId,
      });
    }
  };

  return (
    <EditorWizardContext.Provider
      value={{
        edited,
        locale,
        setLocale,
        updateContent,
        nextStep,
        step,
        loading,
        hideHeaderBar,
        setHideHeaderBar,
        notFound,
        previousStep,
        accepted,
        privacy,
        createdCourse,
        createdSchool,
      }}
    >
      {children}
    </EditorWizardContext.Provider>
  );
};

EditorWizardProvider.propTypes = {
  children: PropTypes.any,
};

EditorWizardProvider.defaultProps = {
  children: null,
};
